import { useEffect, useRef, useCallback, useState } from 'react';

import { getBrowserLocation, getWebSocketConstructor, type WebSocketLike } from './browser';

export interface NotificationStreamEvent {
  type: 'notification' | 'count_update';
  notification?: {
    id: string;
    title?: string;
    body?: string;
    context?: { deepLink?: string; [key: string]: unknown };
    createdAt: string;
  };
  count?: number;
}

export interface UseNotificationStreamOptions {
  /** Whether to connect automatically (default: true) */
  enabled?: boolean;
  /** Callback when a notification event is received */
  onEvent?: (event: NotificationStreamEvent) => void;
  /** Initial reconnection delay in ms (default: 1000) */
  initialReconnectDelay?: number;
  /** Maximum reconnection delay in ms (default: 30000) */
  maxReconnectDelay?: number;
}

export interface UseNotificationStreamResult {
  /** Whether the WebSocket is currently connected */
  isConnected: boolean;
  /** Most recent event received */
  lastEvent: NotificationStreamEvent | null;
  /** Manually reconnect */
  reconnect: () => void;
  /** Manually disconnect */
  disconnect: () => void;
}

/**
 * Hook to subscribe to real-time notifications via WebSocket
 *
 * Features:
 * - Auto-reconnect with exponential backoff
 * - Handles new notification and count update events
 * - Clean disconnection on unmount
 *
 * @param options - Configuration options
 * @returns Connection state and controls
 */
export function useNotificationStream(
  options: UseNotificationStreamOptions = {}
): UseNotificationStreamResult {
  const {
    enabled = true,
    onEvent,
    initialReconnectDelay = 1000,
    maxReconnectDelay = 30000,
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState<NotificationStreamEvent | null>(null);

  const wsRef = useRef<WebSocketLike | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectDelayRef = useRef(initialReconnectDelay);
  const mountedRef = useRef(true);
  const onEventRef = useRef(onEvent);

  // Keep callback ref updated
  onEventRef.current = onEvent;

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const disconnect = useCallback(() => {
    clearReconnectTimeout();
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, [clearReconnectTimeout]);

  const connect = useCallback(() => {
    if (!mountedRef.current || !enabled) return;

    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }

    // Build WebSocket URL
    const location = getBrowserLocation();
    const WebSocketImpl = getWebSocketConstructor();
    if (!location || !WebSocketImpl) return;
    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = location.host;
    const url = `${protocol}//${host}/api/notifications/stream`;

    const ws = new WebSocketImpl(url);
    wsRef.current = ws;

    ws.onopen = () => {
      if (mountedRef.current) {
        setIsConnected(true);
        // Reset reconnect delay on successful connection
        reconnectDelayRef.current = initialReconnectDelay;
      }
    };

    ws.onclose = () => {
      if (mountedRef.current) {
        setIsConnected(false);
        // Schedule reconnection with exponential backoff
        if (enabled) {
          clearReconnectTimeout();
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, reconnectDelayRef.current);
          // Increase delay for next attempt (exponential backoff)
          reconnectDelayRef.current = Math.min(reconnectDelayRef.current * 2, maxReconnectDelay);
        }
      }
    };

    ws.onerror = () => {
      // Error will be followed by close, so we don't need to do much here
      ws.close();
    };

    ws.onmessage = (event) => {
      if (!mountedRef.current) return;

      try {
        const data = JSON.parse(event.data) as NotificationStreamEvent;
        setLastEvent(data);
        onEventRef.current?.(data);
      } catch {
        // Ignore malformed messages
      }
    };
  }, [enabled, initialReconnectDelay, maxReconnectDelay, clearReconnectTimeout]);

  const reconnect = useCallback(() => {
    // Reset delay when manually reconnecting
    reconnectDelayRef.current = initialReconnectDelay;
    disconnect();
    connect();
  }, [disconnect, connect, initialReconnectDelay]);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    mountedRef.current = true;

    if (enabled) {
      connect();
    }

    return () => {
      mountedRef.current = false;
      disconnect();
    };
  }, [enabled, connect, disconnect]);

  return {
    isConnected,
    lastEvent,
    reconnect,
    disconnect,
  };
}
